import React from "react";

import { businessHealthScorePalette } from "../../../../Utils/Helper/businessHealth";
import {
  extractFlattenedFieldAPI,
  isValidField as isValidFieldAPI,
  formatDateAPI,
} from "../../../../Utils/WatchlistSummary/validation";

// ===== TYPES & INTERFACES =====

interface SectionData {
  sectionScore?: number;
  sectionHealthFlag?: string;
  sectionRelevanceFlag?: string;
}

interface LegalCase {
  title?: string;
  case_name?: string;
  description?: string;
  type?: string;
  status?: string;
  risk_flag?: string;
  potential_impact?: string;
  severity_score?: number;
  source_link?: string;
  url?: string;
  source_url?: string;
  court_link?: string;
  date_filed?: string;
  estimated_resolution?: string;
  jurisdiction?: string;
}

interface Risk {
  title?: string;
  description?: string;
}

interface Opportunity {
  title?: string;
  description?: string;
}

interface LegalSectionProps {
  data: any;
  sectionData?: any;
  accountName: string;
  organizationName?: string;
  onToggleAudio?: () => void;
}

// ===== SCORE BUBBLE COMPONENT =====

const LegalScoreBubble: React.FC<{ score: number }> = ({ score }) => {
  const getScoreColors = (score: number) => businessHealthScorePalette(score);

  const colors = getScoreColors(score);

  return (
    <div style={{ position: "relative", width: 48, height: 48, flexShrink: 0 }}>
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: 48,
          height: 48,
          borderRadius: "50%",
          background: colors.outer,
          border: `2px solid ${colors.inner}`,
        }}
      />
      <div
        style={{
          position: "absolute",
          top: 6,
          left: 6,
          width: 36,
          height: 36,
          borderRadius: "50%",
          background: colors.inner,
          border: `1px solid ${colors.score}`,
        }}
      />
      <div
        style={{
          position: "absolute",
          top: -2,
          right: -2,
          width: 20,
          height: 20,
          borderRadius: "50%",
          background: colors.score,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "2px solid white",
        }}
      >
        <span
          style={{
            color: "white",
            fontSize: 11,
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          {Math.round(score)}
        </span>
      </div>
    </div>
  );
};

// ===== MODAL HEADER COMPONENT =====

const LegalModalHeader: React.FC<{
  accountName: string;
  sectionData?: SectionData;
  onToggleAudio?: () => void;
}> = ({ accountName, sectionData, onToggleAudio }) => {
  const sectionScore = sectionData?.sectionScore || 0;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "16px",
        marginBottom: "16px",
      }}
    >
      <LegalScoreBubble score={sectionScore} />
      <div>
        <h3 style={{ margin: 0, fontSize: 18, color: "#8b5cf6" }}>
          <strong>{accountName}</strong> - <em>Legal Analysis</em>
        </h3>
      </div>
      <button
        id="legal-audio-btn"
        title="Play legal audio narration"
        onClick={onToggleAudio}
        style={{
          background: "none",
          border: "none",
          padding: 8,
          borderRadius: 6,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#8b5cf6",
        }}
      >
        <svg
          width={24}
          height={24}
          viewBox="0 0 24 24"
          fill="none"
          stroke="#8b5cf6"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M11 5 6 9H2v6h4l5 4V5Z" />
          <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
          <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
        </svg>
      </button>
    </div>
  );
};

// ===== MODAL BODY COMPONENT =====

const LegalModalBody: React.FC<{
  data: any;
  sectionData?: SectionData;
}> = ({ data, sectionData }) => {
  const synopsis = extractFlattenedFieldAPI(data, "synopsis", null);
  const confidenceScore = extractFlattenedFieldAPI(
    data,
    "metadata.confidence_score",
    null
  );
  const dataFreshness = extractFlattenedFieldAPI(
    data,
    "metadata.data_freshness",
    null
  );
  const sources = extractFlattenedFieldAPI(data, "metadata.sources", []);

  const healthFlag = sectionData?.sectionHealthFlag || "N/A";
  const relevanceFlag = sectionData?.sectionRelevanceFlag || "N/A";

  const sourcesText = Array.isArray(sources)
    ? sources
        .map(s => (typeof s === "object" ? s?.name : s))
        .filter(Boolean)
        .join(", ")
    : "N/A";

  const formatConfidenceScore = (score: any) => {
    if (score === null || score === undefined)
      return { display: "N/A", color: "#64748b" };
    if (typeof score === "number") {
      const percentValue =
        score <= 10 ? Math.round(score * 10) : Math.round(score);
      const display = `${percentValue}%`;
      let color = "#64748b";
      if (percentValue >= 80) color = "#16a34a";
      else if (percentValue >= 50) color = "#d97706";
      else color = "#dc2626";
      return { display, color };
    }
    return { display: "N/A", color: "#64748b" };
  };

  const formatFreshnessValue = (freshness: any) => {
    if (!freshness || freshness === "N/A")
      return { display: "N/A", color: "#64748b" };
    const freshnessStr = String(freshness).toLowerCase();
    let color = "#64748b";
    if (freshnessStr.includes("fresh") || freshnessStr.includes("current"))
      color = "#16a34a";
    else if (freshnessStr.includes("stale") || freshnessStr.includes("old"))
      color = "#dc2626";
    else if (freshnessStr.includes("moderate") || freshnessStr.includes("fair"))
      color = "#d97706";
    return { display: String(freshness), color };
  };

  const confidenceData = formatConfidenceScore(confidenceScore);
  const freshnessData = formatFreshnessValue(dataFreshness);

  const getHealthText = (flag: string) => {
    switch (flag) {
      case "G":
        return "GREEN";
      case "Y":
        return "YELLOW";
      case "R":
        return "RED";
      default:
        return "N/A";
    }
  };

  const getRelevanceText = (flag: string) => {
    switch (flag) {
      case "H":
        return "HIGH";
      case "M":
        return "MEDIUM";
      case "L":
        return "LOW";
      default:
        return "N/A";
    }
  };

  const badgeStyle = (bgColor: string) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 12px",
    background: bgColor,
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 500,
    color: "white",
  });

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        marginBottom: 16,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          gap: 12,
          padding: "12px 16px",
          background: "#f8fafc",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <div
          style={badgeStyle(
            healthFlag === "G"
              ? "#16a34a"
              : healthFlag === "Y"
                ? "#d97706"
                : "#dc2626"
          )}
        >
          <span>Account Health:</span>
          <strong>{getHealthText(healthFlag)}</strong>
        </div>
        <div style={badgeStyle("#8b5cf6")}>
          <span>Relevance to Smarsh:</span>
          <strong>{getRelevanceText(relevanceFlag)}</strong>
        </div>
      </div>

      {synopsis ? (
        <div style={{ padding: 16 }}>
          <p
            style={{
              margin: "0 0 12px 0",
              lineHeight: 1.6,
              fontSize: 14,
              color: "#374151",
            }}
          >
            {synopsis}
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "20% 30% 50%",
              gap: 12,
              marginTop: 12,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 11,
                padding: "6px 8px",
                background: "#f8fafc",
                borderRadius: 4,
              }}
            >
              <span style={{ color: "#64748b", fontWeight: 500 }}>
                Confidence:
              </span>
              <span
                style={{
                  color: confidenceData.color,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                {confidenceData.display}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                fontSize: 11,
                padding: "6px 8px",
                background: "#f8fafc",
                borderRadius: 4,
              }}
            >
              <span
                style={{ color: "#64748b", fontWeight: 500, marginBottom: 2 }}
              >
                Freshness:
              </span>
              <span
                style={{
                  color: freshnessData.color,
                  fontWeight: 600,
                  wordWrap: "break-word",
                  whiteSpace: "normal",
                  lineHeight: 1.3,
                }}
              >
                {freshnessData.display}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                fontSize: 11,
                padding: "6px 8px",
                background: "#f8fafc",
                borderRadius: 4,
              }}
            >
              <span
                style={{ color: "#64748b", fontWeight: 500, marginBottom: 2 }}
              >
                Sources:
              </span>
              <span
                style={{
                  color: "#1e293b",
                  fontWeight: 600,
                  wordWrap: "break-word",
                  whiteSpace: "normal",
                  lineHeight: 1.3,
                }}
              >
                {sourcesText}
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ padding: 16 }}>
          <p
            style={{
              margin: 0,
              color: "#64748b",
              fontStyle: "italic",
              fontSize: 14,
            }}
          >
            No legal metadata available for this account.
          </p>
        </div>
      )}
    </div>
  );
};

// ===== ORGANIZATION RELEVANCE CARD =====

const LegalOrganizationRelevanceCard: React.FC<{
  data: any;
  organizationName?: string;
}> = ({ data, organizationName }) => {
  const orgRelevance = extractFlattenedFieldAPI(
    data,
    "organization_related",
    []
  );
  const relevantToOrganization =
    Array.isArray(orgRelevance) && orgRelevance.length > 0
      ? orgRelevance[0]
      : null;

  if (!relevantToOrganization) {
    return (
      <div
        style={{
          background: "white",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          marginBottom: 16,
          padding: 16,
        }}
      >
        <h4
          style={{
            margin: "0 0 12px 0",
            fontSize: 16,
            fontWeight: 600,
            color: "#374151",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span>⚖️</span> Relevance for {organizationName || "Organization"}
        </h4>
        <p
          style={{
            margin: 0,
            color: "#64748b",
            fontStyle: "italic",
            fontSize: 14,
          }}
        >
          No organization relevance data available.
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        marginBottom: 16,
        padding: 16,
      }}
    >
      <h4
        style={{
          margin: "0 0 12px 0",
          fontSize: 16,
          fontWeight: 600,
          color: "#374151",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span>⚖️</span> Relevance for {organizationName || "Organization"}
      </h4>
      <div
        style={{
          background: "#f3f4f6",
          borderLeft: "4px solid #8b5cf6",
          padding: "12px 16px",
          borderRadius: 8,
        }}
      >
        <p
          style={{
            margin: 0,
            lineHeight: 1.6,
            fontSize: 14,
            color: "#374151",
            fontWeight: 500,
          }}
        >
          {relevantToOrganization.description ||
            "Legal and regulatory compliance considerations present opportunities for enhanced oversight solutions."}
        </p>
      </div>
    </div>
  );
};

// ===== KEY METRICS CARD =====

const LegalKeyMetricsCard: React.FC<{ data: any }> = ({ data }) => {
  const activeCasesCount = extractFlattenedFieldAPI(
    data,
    "kpis.active_cases_count",
    null
  );
  const trendDirection = extractFlattenedFieldAPI(
    data,
    "kpis.twelve_month_trend.trend_direction",
    null
  );
  const regulatoryFinesTotal = extractFlattenedFieldAPI(
    data,
    "kpis.regulatory_fines_total",
    null
  );
  const averageTimeToResolution = extractFlattenedFieldAPI(
    data,
    "kpis.average_time_to_resolution_months",
    null
  );

  const hasData =
    isValidFieldAPI(activeCasesCount) ||
    isValidFieldAPI(trendDirection) ||
    isValidFieldAPI(regulatoryFinesTotal) ||
    isValidFieldAPI(averageTimeToResolution);

  if (!hasData) {
    return (
      <div
        style={{
          background: "white",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          marginBottom: 16,
          padding: 16,
        }}
      >
        <h4
          style={{
            margin: "0 0 12px 0",
            fontSize: 16,
            fontWeight: 600,
            color: "#374151",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span>📊</span> Key Legal Metrics
        </h4>
        <p
          style={{
            margin: 0,
            color: "#64748b",
            fontStyle: "italic",
            fontSize: 14,
          }}
        >
          No key metrics data available.
        </p>
      </div>
    );
  }

  const getTrendColor = (trend: string) => {
    if (!trend) return "#64748b";
    const lowerTrend = trend.toLowerCase();
    if (lowerTrend === "increasing") return "#dc2626";
    if (lowerTrend === "decreasing") return "#16a34a";
    if (lowerTrend === "stable") return "#d97706";
    return "#64748b";
  };

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        marginBottom: 16,
        padding: 16,
      }}
    >
      <h4
        style={{
          margin: "0 0 12px 0",
          fontSize: 16,
          fontWeight: 600,
          color: "#374151",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span>📊</span> Key Legal Metrics
      </h4>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 12,
        }}
      >
        {isValidFieldAPI(activeCasesCount) && (
          <div style={{ background: "#f8fafc", padding: 10, borderRadius: 8 }}>
            <div
              style={{
                fontSize: 11,
                color: "#64748b",
                fontWeight: 500,
                marginBottom: 4,
              }}
            >
              ACTIVE CASES
            </div>
            <div style={{ fontSize: 12, color: "#1e293b", fontWeight: 600 }}>
              {activeCasesCount}
            </div>
          </div>
        )}
        {isValidFieldAPI(trendDirection) && (
          <div style={{ background: "#f8fafc", padding: 10, borderRadius: 8 }}>
            <div
              style={{
                fontSize: 11,
                color: "#64748b",
                fontWeight: 500,
                marginBottom: 4,
              }}
            >
              TREND
            </div>
            <div
              style={{
                fontSize: 12,
                color: getTrendColor(trendDirection),
                fontWeight: 600,
                textTransform: "capitalize",
              }}
            >
              {trendDirection}
            </div>
          </div>
        )}
        {isValidFieldAPI(regulatoryFinesTotal) && (
          <div style={{ background: "#f8fafc", padding: 10, borderRadius: 8 }}>
            <div
              style={{
                fontSize: 11,
                color: "#64748b",
                fontWeight: 500,
                marginBottom: 4,
              }}
            >
              FINES
            </div>
            <div style={{ fontSize: 12, color: "#dc2626", fontWeight: 600 }}>
              {regulatoryFinesTotal}
            </div>
          </div>
        )}
        {isValidFieldAPI(averageTimeToResolution) && (
          <div style={{ background: "#f8fafc", padding: 10, borderRadius: 8 }}>
            <div
              style={{
                fontSize: 11,
                color: "#64748b",
                fontWeight: 500,
                marginBottom: 4,
              }}
            >
              AVG RESOLUTION
            </div>
            <div style={{ fontSize: 12, color: "#1e293b", fontWeight: 600 }}>
              {averageTimeToResolution} mo
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ===== LEGAL CASES CARD =====

const LegalCasesCard: React.FC<{ data: any }> = ({ data }) => {
  const cases = extractFlattenedFieldAPI(data, "cases", []);
  const casesArray: LegalCase[] = Array.isArray(cases) ? cases : [];

  if (casesArray.length === 0) {
    return (
      <div
        style={{
          background: "white",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          marginBottom: 16,
          padding: 16,
        }}
      >
        <h4
          style={{
            margin: "0 0 12px 0",
            fontSize: 16,
            fontWeight: 600,
            color: "#374151",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span>⚖️</span> Legal Cases
        </h4>
        <p
          style={{
            margin: 0,
            color: "#64748b",
            fontStyle: "italic",
            fontSize: 14,
          }}
        >
          No legal cases data available.
        </p>
      </div>
    );
  }

  const getStatusColor = (status?: string) => {
    if (!status) return "#64748b";
    const lowerStatus = status.toLowerCase();
    if (lowerStatus === "resolved") return "#16a34a";
    if (lowerStatus === "pending") return "#d97706";
    if (lowerStatus === "ongoing") return "#dc2626";
    return "#64748b";
  };

  const getRiskFlagColor = (riskFlag?: string) => {
    if (!riskFlag) return "#64748b";
    const lowerRisk = riskFlag.toLowerCase();
    if (lowerRisk === "high") return "#dc2626";
    if (lowerRisk === "medium") return "#d97706";
    if (lowerRisk === "low") return "#16a34a";
    return "#64748b";
  };

  const getCaseTypeColor = (type?: string) => {
    if (!type) return "#6b7280";
    const lowerType = type.toLowerCase();
    if (lowerType.includes("lawsuit") || lowerType.includes("litigation"))
      return "#7c3aed";
    if (lowerType.includes("regulatory")) return "#dc2626";
    if (lowerType.includes("investigation")) return "#d97706";
    if (lowerType.includes("settlement")) return "#16a34a";
    return "#6b7280";
  };

  const getImpactBadgeColor = (impact?: string) => {
    if (!impact) return "#6b7280";
    const lowerImpact = impact.toLowerCase();
    if (lowerImpact === "high") return "#dc2626";
    if (lowerImpact === "medium") return "#d97706";
    if (lowerImpact === "low") return "#16a34a";
    return "#6b7280";
  };

  const getSeverityColor = (severity?: number) => {
    if (severity === null || severity === undefined) return "#64748b";
    if (severity >= 8) return "#dc2626";
    if (severity >= 5) return "#d97706";
    if (severity >= 1) return "#16a34a";
    return "#64748b";
  };

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        marginBottom: 16,
        padding: 16,
      }}
    >
      <h4
        style={{
          margin: "0 0 12px 0",
          fontSize: 16,
          fontWeight: 600,
          color: "#374151",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span>⚖️</span> Legal Cases
      </h4>
      <div>
        {casesArray.slice(0, 5).map((caseItem, index) => {
          const url =
            caseItem.source_link ||
            caseItem.url ||
            caseItem.source_url ||
            caseItem.court_link;
          const title = caseItem.title || caseItem.case_name || "Legal Case";

          return (
            <div
              key={index}
              style={{
                background: "#f8fafc",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                padding: 12,
                marginBottom: 12,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "start",
                  marginBottom: 8,
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ marginBottom: 6 }}>
                    {url ? (
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#2563eb",
                          textDecoration: "none",
                          fontWeight: 600,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        {title}
                        <svg
                          width={12}
                          height={12}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                          <polyline points="15,3 21,3 21,9" />
                          <line x1="10" y1="14" x2="21" y2="3" />
                        </svg>
                      </a>
                    ) : (
                      <span style={{ fontWeight: 600, color: "#374151" }}>
                        {title}
                      </span>
                    )}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 6,
                      marginBottom: 8,
                    }}
                  >
                    {caseItem.type && (
                      <span
                        style={{
                          background: getCaseTypeColor(caseItem.type),
                          color: "white",
                          padding: "2px 8px",
                          borderRadius: 12,
                          fontSize: 10,
                          fontWeight: 500,
                          textTransform: "uppercase",
                        }}
                      >
                        {caseItem.type}
                      </span>
                    )}
                    {caseItem.status && (
                      <span
                        style={{
                          background: "#f1f5f9",
                          color: getStatusColor(caseItem.status),
                          padding: "2px 8px",
                          borderRadius: 12,
                          fontSize: 10,
                          fontWeight: 500,
                          textTransform: "uppercase",
                        }}
                      >
                        {caseItem.status}
                      </span>
                    )}
                    {caseItem.risk_flag && (
                      <span
                        style={{
                          background: getRiskFlagColor(caseItem.risk_flag),
                          color: "white",
                          padding: "2px 8px",
                          borderRadius: 12,
                          fontSize: 10,
                          fontWeight: 500,
                          textTransform: "uppercase",
                        }}
                      >
                        Risk: {caseItem.risk_flag}
                      </span>
                    )}
                    {caseItem.potential_impact && (
                      <span
                        style={{
                          background: getImpactBadgeColor(
                            caseItem.potential_impact
                          ),
                          color: "white",
                          padding: "2px 8px",
                          borderRadius: 12,
                          fontSize: 10,
                          fontWeight: 500,
                          textTransform: "uppercase",
                        }}
                      >
                        Impact: {caseItem.potential_impact}
                      </span>
                    )}
                    {caseItem.severity_score !== null &&
                      caseItem.severity_score !== undefined && (
                        <span
                          style={{
                            background: getSeverityColor(
                              caseItem.severity_score
                            ),
                            color: "white",
                            padding: "2px 8px",
                            borderRadius: 12,
                            fontSize: 10,
                            fontWeight: 500,
                          }}
                        >
                          Severity: {caseItem.severity_score}/10
                        </span>
                      )}
                  </div>
                </div>
              </div>

              {caseItem.description && (
                <p
                  style={{
                    margin: "8px 0",
                    color: "#374151",
                    lineHeight: 1.4,
                    fontSize: 13,
                  }}
                >
                  {caseItem.description}
                </p>
              )}

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                  gap: 8,
                  marginTop: 8,
                  fontSize: 11,
                  color: "#64748b",
                }}
              >
                {caseItem.date_filed && (
                  <div>
                    <strong>Filed:</strong> {formatDateAPI(caseItem.date_filed)}
                  </div>
                )}
                {caseItem.estimated_resolution && (
                  <div>
                    <strong>Est. Resolution:</strong>{" "}
                    {caseItem.estimated_resolution}
                  </div>
                )}
                {caseItem.jurisdiction && (
                  <div>
                    <strong>Jurisdiction:</strong> {caseItem.jurisdiction}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ===== RISKS & OPPORTUNITIES CARD =====

const LegalRisksOpportunitiesCard: React.FC<{ data: any }> = ({ data }) => {
  const riskAssessment = extractFlattenedFieldAPI(data, "risk_assessment", {});
  const opportunities = extractFlattenedFieldAPI(data, "opportunities", []);

  const risks: (Risk | string)[] = riskAssessment?.key_risks || [];
  const opportunitiesArray: (Opportunity | string)[] = Array.isArray(
    opportunities
  )
    ? opportunities
    : [];

  const hasData = risks.length > 0 || opportunitiesArray.length > 0;

  if (!hasData) {
    return (
      <div
        style={{
          background: "white",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          marginBottom: 16,
          padding: 16,
        }}
      >
        <h4
          style={{
            margin: "0 0 12px 0",
            fontSize: 16,
            fontWeight: 600,
            color: "#374151",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span>⚠️</span> Risks & Opportunities
        </h4>
        <p
          style={{
            margin: 0,
            color: "#64748b",
            fontStyle: "italic",
            fontSize: 14,
          }}
        >
          No risks and opportunities data available.
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        marginBottom: 16,
        padding: 16,
      }}
    >
      <h4
        style={{
          margin: "0 0 12px 0",
          fontSize: 16,
          fontWeight: 600,
          color: "#374151",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span>⚠️</span> Risks & Opportunities
      </h4>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div>
          <h5
            style={{
              margin: "0 0 8px 0",
              fontSize: 14,
              fontWeight: 600,
              color: "#dc2626",
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <span>🚨</span> Risk Factors
          </h5>
          {risks.length > 0 ? (
            risks.map((risk, i) => {
              const riskTitle =
                typeof risk === "string"
                  ? risk
                  : risk.title || risk.description || "Risk Factor";
              const riskDesc =
                typeof risk === "object" && risk.description
                  ? risk.description
                  : null;

              return (
                <div
                  key={i}
                  style={{
                    background: "#fef2f2",
                    border: "1px solid #fecaca",
                    borderRadius: 6,
                    padding: 8,
                    marginBottom: 6,
                  }}
                >
                  <div
                    style={{
                      fontWeight: 600,
                      color: "#991b1b",
                      fontSize: 13,
                      marginBottom: 2,
                    }}
                  >
                    {riskTitle}
                  </div>
                  {riskDesc && (
                    <div
                      style={{
                        color: "#374151",
                        fontSize: 12,
                        lineHeight: 1.3,
                      }}
                    >
                      {riskDesc}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <p style={{ color: "#64748b", fontStyle: "italic", fontSize: 12 }}>
              No significant risk factors identified
            </p>
          )}
        </div>

        <div>
          <h5
            style={{
              margin: "0 0 8px 0",
              fontSize: 14,
              fontWeight: 600,
              color: "#16a34a",
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <span>✅</span> Opportunities
          </h5>
          {opportunitiesArray.length > 0 ? (
            opportunitiesArray.map((opp, i) => {
              const oppTitle =
                typeof opp === "string"
                  ? opp
                  : opp.title || opp.description || "Opportunity";
              const oppDesc =
                typeof opp === "object" && opp.description
                  ? opp.description
                  : null;

              return (
                <div
                  key={i}
                  style={{
                    background: "#f0fdf4",
                    border: "1px solid #bbf7d0",
                    borderRadius: 6,
                    padding: 8,
                    marginBottom: 6,
                  }}
                >
                  <div
                    style={{
                      fontWeight: 600,
                      color: "#065f46",
                      fontSize: 13,
                      marginBottom: 2,
                    }}
                  >
                    {oppTitle}
                  </div>
                  {oppDesc && (
                    <div
                      style={{
                        color: "#374151",
                        fontSize: 12,
                        lineHeight: 1.3,
                      }}
                    >
                      {oppDesc}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <p style={{ color: "#64748b", fontStyle: "italic", fontSize: 12 }}>
              No specific opportunities identified
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// ===== MAIN LEGAL SECTION COMPONENT =====

export const LegalSection: React.FC<LegalSectionProps> = ({
  data,
  sectionData,
  accountName,
  organizationName,
  onToggleAudio,
}) => {
  if (!data) {
    return <div>No legal data available</div>;
  }

  return (
    <div className="legal-section-consolidated">
      <LegalModalHeader
        accountName={accountName}
        sectionData={sectionData}
        onToggleAudio={onToggleAudio}
      />
      <LegalModalBody data={data} sectionData={sectionData} />
      <LegalOrganizationRelevanceCard
        data={data}
        organizationName={organizationName}
      />
      <LegalKeyMetricsCard data={data} />
      <LegalCasesCard data={data} />
      <LegalRisksOpportunitiesCard data={data} />
    </div>
  );
};
